// features/super-admin/pages/SubscriptionsPage.tsx
/**
 * SUPER ADMIN — SUBSCRIPTIONS & BILLING
 * Enterprise-grade subscription management with invoicing.
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { useToastStore } from '@/store/toastStore';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import { Card, CardContent, CardHeader } from '@/components/ui/Card';
import apiClient from '@/lib/api-client';
import {
    ArrowLeft, Search, CreditCard, ChevronLeft, ChevronRight,
    ChevronsLeft, ChevronsRight, X, Calendar,
    Building2, DollarSign, Clock, CheckCircle2, XCircle,
    AlertTriangle, RefreshCw, Eye,
    Receipt, Send, Plus, TrendingUp, TrendingDown,
    Layers, Star, Award, Zap, Sliders, Save, HardDrive, Server, Phone, Mail, Check, MessageSquare, Smartphone,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx } from 'clsx';

// ============================================================
// TYPES
// ============================================================

interface Subscription {
    id: string; tenantId: string; tenantName: string; tenantSlug: string;
    planId: string; planName: string; planCode: string;
    status: 'trial' | 'active' | 'past_due' | 'cancelled' | 'expired';
    trialStartAt: string | null; trialEndAt: string | null;
    currentPeriodStart: string | null; currentPeriodEnd: string | null;
    cancelAtPeriodEnd: boolean; amount: number; currency: string;
    createdAt: string;
}

interface Invoice {
    id: string; invoiceNumber: string; tenantId: string; tenantName: string;
    subscriptionId: string; amount: number; currency: string;
    status: 'pending' | 'paid' | 'overdue' | 'cancelled' | 'refunded';
    dueDate: string; paidAt: string | null; paymentMethod: string | null;
    createdAt: string;
}

interface SubscriptionsResponse {
    subscriptions: Subscription[]; totalCount: number;
    summary: { totalActive: number; totalTrialing: number; totalPastDue: number; totalCancelled: number; monthlyRecurringRevenue: string };
}

interface InvoicesResponse {
    invoices: Invoice[]; totalCount: number;
    summary: { totalPending: number; totalPaid: number; totalOverdue: number; totalAmount: string };
}

// ============================================================
// CONSTANTS
// ============================================================

const STATUS_CONFIG: Record<string, { variant: 'success' | 'warning' | 'danger' | 'neutral' | 'info'; icon: React.ElementType; label: string }> = {
    active: { variant: 'success', icon: CheckCircle2, label: 'Active' },
    trial: { variant: 'info', icon: Clock, label: 'Trial' },
    past_due: { variant: 'danger', icon: AlertTriangle, label: 'Past Due' },
    cancelled: { variant: 'neutral', icon: XCircle, label: 'Cancelled' },
    expired: { variant: 'danger', icon: XCircle, label: 'Expired' },
};

const INVOICE_STATUS: Record<string, { variant: 'success' | 'warning' | 'danger' | 'neutral' | 'info'; label: string }> = {
    pending: { variant: 'warning', label: 'Pending' },
    paid: { variant: 'success', label: 'Paid' },
    overdue: { variant: 'danger', label: 'Overdue' },
    cancelled: { variant: 'neutral', label: 'Cancelled' },
    refunded: { variant: 'info', label: 'Refunded' },
};

const PLAN_CONFIG: Record<string, { icon: React.ElementType; color: string }> = {
    enterprise: { icon: Award, color: 'text-amber-600' },
    business: { icon: Building2, color: 'text-purple-600' },
    starter: { icon: Zap, color: 'text-blue-600' },
    free: { icon: Star, color: 'text-slate-600' },
};

// ============================================================
// FORMATTERS
// ============================================================

const formatNaira = (val: string | number) => {
    const num = typeof val === 'string' ? parseFloat(val) : val;
    if (isNaN(num)) return '₦0';
    if (num >= 1_000_000) return `₦${(num / 1_000_000).toFixed(1)}M`;
    return `₦${num.toLocaleString('en-NG')}`;
};

const formatDate = (date: string) => {
    try { return new Date(date).toLocaleDateString('en-NG', { day: 'numeric', month: 'short', year: 'numeric' }); }
    catch { return 'N/A'; }
};

// ============================================================
// MAIN COMPONENT
// ============================================================

export function SubscriptionsPage() {
    const navigate = useNavigate();
    const user = useAuthStore(s => s.user);
    const queryClient = useQueryClient();
    const addToast = useToastStore(s => s.addToast);

    const [activeTab, setActiveTab] = useState<'subscriptions' | 'invoices' | 'settings'>('subscriptions');
    const [subPage, setSubPage] = useState(1);
    const [subFilters, setSubFilters] = useState({ search: '', status: '' });
    const [invPage, setInvPage] = useState(1);
    const [invFilters, setInvFilters] = useState({ search: '', status: '' });
    const [showInvoiceModal, setShowInvoiceModal] = useState(false);
    const [invoiceForm, setInvoiceForm] = useState({ tenantId: '', amount: 0, currency: 'NGN', dueDate: '', notes: '' });
    const [tenantSearch, setTenantSearch] = useState('');
    const [showTenantDropdown, setShowTenantDropdown] = useState(false);
    const [selectedSubscription, setSelectedSubscription] = useState<Subscription | null>(null);

    const { data: subData } = useQuery<SubscriptionsResponse>({
        queryKey: ['super-admin-subscriptions', subPage, subFilters],
        queryFn: async () => {
            const { data } = await apiClient.get('/super-admin/subscriptions', { params: { limit: 15, page: subPage, ...subFilters } });
            return data?.data;
        },
        enabled: !!user,
    });

    const { data: invData } = useQuery<InvoicesResponse>({
        queryKey: ['super-admin-invoices', invPage, invFilters],
        queryFn: async () => {
            const { data } = await apiClient.get('/super-admin/invoices', { params: { limit: 15, page: invPage, ...invFilters } });
            return data?.data;
        },
        enabled: !!user && activeTab === 'invoices',
    });

    const { data: tenantsList } = useQuery<Array<{ id: string; name: string; slug: string }>>({
        queryKey: ['super-admin-tenants-list'],
        queryFn: async () => {
            const { data } = await apiClient.get('/super-admin/tenants', { params: { limit: 200 } });
            return data?.data?.tenants || [];
        },
        enabled: !!user,
    });

    const createInvoiceMutation = useMutation({
        mutationFn: (payload: any) => apiClient.post('/super-admin/invoices', payload),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['super-admin-invoices'] });
            addToast({ type: 'success', title: 'Invoice Created' });
            setShowInvoiceModal(false);
            setInvoiceForm({ tenantId: '', amount: 0, currency: 'NGN', dueDate: '', notes: '' });
        },
        onError: (err: any) => addToast({ type: 'error', title: 'Failed', message: err?.message }),
    });

    const sendInvoiceMutation = useMutation({
        mutationFn: (invoiceId: string) => apiClient.post(`/super-admin/invoices/${invoiceId}/send`),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['super-admin-invoices'] });
            addToast({ type: 'success', title: 'Invoice Sent' });
        },
    });

    const quickExtendSubMutation = useMutation({
        mutationFn: ({ tenantId, days, currentEnd }: { tenantId: string; days: number; currentEnd?: string | null }) => {
            const baseDate = currentEnd ? new Date(currentEnd) : new Date();
            const effectiveBase = baseDate.getTime() > Date.now() ? baseDate : new Date();
            const newExpiry = new Date(effectiveBase.getTime() + days * 24 * 60 * 60 * 1000);
            return apiClient.post(`/super-admin/tenants/${tenantId}/subscription`, {
                expiresAt: newExpiry.toISOString(),
                status: 'active',
                isSubscribed: true,
            });
        },
        onSuccess: (_res, vars) => {
            queryClient.invalidateQueries({ queryKey: ['super-admin-subscriptions'] });
            addToast({
                type: 'success',
                title: 'Subscription Extended',
                message: `Added +${vars.days} days to subscription!`,
            });
        },
        onError: (err: any) => addToast({ type: 'error', title: 'Extension Failed', message: err?.message }),
    });

    const sendWhatsAppReminder = (inv: Invoice) => {
        const text = encodeURIComponent(
            `*EtiPOS Subscription Payment Reminder*\n\n` +
            `Hello ${inv.tenantName},\n` +
            `This is a friendly reminder regarding your EtiPOS invoice *#${inv.invoiceNumber}* for *${formatNaira(inv.amount)}*, due on *${formatDate(inv.dueDate)}*.\n\n` +
            `To keep your store operations active without interruption, kindly make payment and reply with your proof of payment.\n\n` +
            `Thank you for choosing EtiPOS!`
        );
        window.open(`https://wa.me/?text=${text}`, '_blank');
    };

    const subscriptions = subData?.subscriptions || [];
    const subSummary = subData?.summary;
    const invoices = invData?.invoices || [];
    const invSummary = invData?.summary;
    const filteredTenants = (tenantsList || []).filter(t => !tenantSearch || t.name.toLowerCase().includes(tenantSearch.toLowerCase()));
    const selectedTenant = tenantsList?.find(t => t.id === invoiceForm.tenantId);

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-violet-50/30">
            <div className="max-w-[1800px] mx-auto p-4 lg:p-6 space-y-5">

                {/* Header */}
                <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
                    <button onClick={() => navigate('/super-admin')}
                        className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-violet-600 mb-3 group">
                        <ArrowLeft size={16} className="group-hover:-translate-x-1" /> Back to Dashboard
                    </button>
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-lg">
                                <CreditCard size={20} className="text-white" />
                            </div>
                            <div>
                                <h1 className="text-2xl lg:text-3xl font-bold text-slate-900">Subscriptions & Billing</h1>
                                <p className="text-slate-500 text-sm mt-0.5">Manage tenant subscriptions and invoices</p>
                            </div>
                        </div>
                        <button onClick={() => setShowInvoiceModal(true)}
                            className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-violet-600 to-purple-700 text-white rounded-xl font-semibold shadow-lg text-sm">
                            <Plus size={16} /> Create Invoice
                        </button>
                    </div>
                </motion.div>

                {/* Tabs */}
                <div className="flex gap-1 bg-white rounded-2xl border border-slate-200 shadow-sm p-1.5 w-fit">
                    <button onClick={() => setActiveTab('subscriptions')}
                        className={clsx('flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all',
                            activeTab === 'subscriptions' ? 'bg-violet-100 text-violet-700 shadow-sm' : 'text-slate-500 hover:text-slate-700')}>
                        <CreditCard size={16} /> Subscriptions
                    </button>
                    <button onClick={() => setActiveTab('invoices')}
                        className={clsx('flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all',
                            activeTab === 'invoices' ? 'bg-violet-100 text-violet-700 shadow-sm' : 'text-slate-500 hover:text-slate-700')}>
                        <Receipt size={16} /> Invoices
                    </button>
                    <button onClick={() => setActiveTab('settings')}
                        className={clsx('flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all',
                            activeTab === 'settings' ? 'bg-violet-100 text-violet-700 shadow-sm' : 'text-slate-500 hover:text-slate-700')}>
                        <Sliders size={16} /> Plan & Bank Settings
                    </button>
                </div>

                {/* SUBSCRIPTIONS TAB */}
                {activeTab === 'subscriptions' && (
                    <>
                        {subSummary && (
                            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                                {[
                                    { label: 'Active', value: subSummary.totalActive, icon: CheckCircle2, gradient: 'from-emerald-500 to-teal-600' },
                                    { label: 'Trialing', value: subSummary.totalTrialing, icon: Clock, gradient: 'from-blue-500 to-indigo-600' },
                                    { label: 'Past Due', value: subSummary.totalPastDue, icon: AlertTriangle, gradient: 'from-amber-500 to-orange-600' },
                                    { label: 'Cancelled', value: subSummary.totalCancelled, icon: XCircle, gradient: 'from-rose-500 to-pink-600' },
                                    { label: 'MRR', value: formatNaira(subSummary.monthlyRecurringRevenue), icon: DollarSign, gradient: 'from-violet-500 to-purple-600' },
                                ].map((stat, i) => (
                                    <motion.div key={stat.label} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                                        className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
                                        <div className={`p-2 rounded-lg bg-gradient-to-br ${stat.gradient} shadow-md w-fit mb-3`}><stat.icon className="w-4 h-4 text-white" /></div>
                                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">{stat.label}</p>
                                        <p className="text-xl font-bold text-slate-900 mt-0.5">{stat.value}</p>
                                    </motion.div>
                                ))}
                            </div>
                        )}

                        <div className="flex items-center gap-2 bg-white rounded-2xl border border-slate-200 shadow-sm p-3">
                            <div className="relative flex-1">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                <input type="text" placeholder="Search by tenant name..." value={subFilters.search}
                                    onChange={e => { setSubFilters({ ...subFilters, search: e.target.value }); setSubPage(1); }}
                                    className="w-full pl-10 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm" />
                            </div>
                            <select value={subFilters.status} onChange={e => { setSubFilters({ ...subFilters, status: e.target.value }); setSubPage(1); }}
                                className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium">
                                <option value="">All Statuses</option>
                                <option value="active">Active</option><option value="trial">Trial</option>
                                <option value="past_due">Past Due</option><option value="cancelled">Cancelled</option>
                            </select>
                        </div>

                        {subscriptions.length === 0 ? (
                            <EmptyState icon={<CreditCard size={48} />} title="No subscriptions found" />
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {subscriptions.map((sub, i) => {
                                    const sc = STATUS_CONFIG[sub.status] || STATUS_CONFIG.active;
                                    const Icon = sc.icon;
                                    const planConfig = PLAN_CONFIG[sub.planCode] || PLAN_CONFIG.free;
                                    const PlanIcon = planConfig.icon;
                                    return (
                                        <motion.div key={sub.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}
                                            whileHover={{ y: -4 }}
                                            className="bg-white rounded-2xl border-2 border-slate-200 shadow-sm p-5 hover:border-violet-300 hover:shadow-lg transition-all cursor-pointer"
                                            onClick={() => setSelectedSubscription(selectedSubscription?.id === sub.id ? null : sub)}>
                                            <div className="flex items-start justify-between mb-3">
                                                <div className="flex items-center gap-2">
                                                    <Building2 size={14} className="text-violet-400" />
                                                    <div>
                                                        <p className="font-bold text-sm text-slate-800">{sub.tenantName}</p>
                                                        <code className="text-[10px] text-slate-400">{sub.tenantSlug}</code>
                                                    </div>
                                                </div>
                                                <Badge variant={sc.variant} size="sm"><Icon size={10} className="mr-1" />{sc.label}</Badge>
                                            </div>
                                            <div className="flex items-center gap-2 mb-3">
                                                <PlanIcon size={14} className={planConfig.color} />
                                                <span className="text-xs font-medium text-slate-600 capitalize">{sub.planName || sub.planCode}</span>
                                            </div>
                                            <div className="grid grid-cols-2 gap-2 pt-3 border-t border-slate-100 text-xs text-slate-500 mb-3">
                                                <span>Period: {sub.currentPeriodStart ? formatDate(sub.currentPeriodStart) : '—'}</span>
                                                <span>Ends: {sub.currentPeriodEnd ? formatDate(sub.currentPeriodEnd) : '—'}</span>
                                            </div>
                                            <div className="flex items-center gap-2 pt-2 border-t border-slate-100" onClick={e => e.stopPropagation()}>
                                                <button
                                                    onClick={() => navigate(`/super-admin/tenants/${sub.tenantId}`)}
                                                    className="flex-1 py-1.5 px-2.5 text-xs font-semibold text-violet-700 bg-violet-50 hover:bg-violet-100 border border-violet-200 rounded-xl transition-colors flex items-center justify-center gap-1.5">
                                                    <Eye size={13} /> Manage
                                                </button>
                                                <button
                                                    disabled={quickExtendSubMutation.isPending}
                                                    onClick={() => quickExtendSubMutation.mutate({ tenantId: sub.tenantId, days: 30, currentEnd: sub.currentPeriodEnd })}
                                                    className="flex-1 py-1.5 px-2.5 text-xs font-semibold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 rounded-xl transition-colors flex items-center justify-center gap-1.5">
                                                    <Clock size={13} /> +30 Days
                                                </button>
                                            </div>
                                        </motion.div>
                                    );
                                })}
                            </div>
                        )}
                    </>
                )}

                {/* INVOICES TAB */}
                {activeTab === 'invoices' && (
                    <>
                        {invSummary && (
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                {[
                                    { label: 'Pending', value: invSummary.totalPending, icon: Clock, gradient: 'from-amber-500 to-orange-600' },
                                    { label: 'Paid', value: invSummary.totalPaid, icon: CheckCircle2, gradient: 'from-emerald-500 to-teal-600' },
                                    { label: 'Overdue', value: invSummary.totalOverdue, icon: AlertTriangle, gradient: 'from-rose-500 to-pink-600' },
                                    { label: 'Total', value: formatNaira(invSummary.totalAmount), icon: DollarSign, gradient: 'from-blue-500 to-indigo-600' },
                                ].map((stat, i) => (
                                    <motion.div key={stat.label} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                                        className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
                                        <div className={`p-2 rounded-lg bg-gradient-to-br ${stat.gradient} shadow-md w-fit mb-3`}><stat.icon className="w-4 h-4 text-white" /></div>
                                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">{stat.label}</p>
                                        <p className="text-xl font-bold text-slate-900 mt-0.5">{stat.value}</p>
                                    </motion.div>
                                ))}
                            </div>
                        )}

                        <div className="flex items-center gap-2 bg-white rounded-2xl border border-slate-200 shadow-sm p-3">
                            <div className="relative flex-1">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                <input type="text" placeholder="Search invoices..." value={invFilters.search}
                                    onChange={e => { setInvFilters({ ...invFilters, search: e.target.value }); setInvPage(1); }}
                                    className="w-full pl-10 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm" />
                            </div>
                            <select value={invFilters.status} onChange={e => { setInvFilters({ ...invFilters, status: e.target.value }); setInvPage(1); }}
                                className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium">
                                <option value="">All</option>
                                <option value="pending">Pending</option><option value="paid">Paid</option><option value="overdue">Overdue</option>
                            </select>
                        </div>

                        {invoices.length === 0 ? (
                            <EmptyState icon={<Receipt size={48} />} title="No invoices" />
                        ) : (
                            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                                <table className="min-w-full divide-y divide-slate-100">
                                    <thead><tr className="bg-slate-50/50">
                                        <th className="px-4 py-3 text-left text-[10px] font-bold text-slate-500 uppercase">Invoice #</th>
                                        <th className="px-4 py-3 text-left text-[10px] font-bold text-slate-500 uppercase">Tenant</th>
                                        <th className="px-4 py-3 text-right text-[10px] font-bold text-slate-500 uppercase">Amount</th>
                                        <th className="px-4 py-3 text-center text-[10px] font-bold text-slate-500 uppercase">Status</th>
                                        <th className="px-4 py-3 text-left text-[10px] font-bold text-slate-500 uppercase hidden md:table-cell">Due Date</th>
                                        <th className="px-4 py-3 text-right text-[10px] font-bold text-slate-500 uppercase w-28">Actions</th>
                                    </tr></thead>
                                    <tbody className="divide-y divide-slate-50">
                                        {invoices.map(inv => {
                                            const sc = INVOICE_STATUS[inv.status] || INVOICE_STATUS.pending;
                                            return (
                                                <tr key={inv.id} className="hover:bg-slate-50">
                                                    <td className="px-4 py-3"><code className="text-xs font-mono font-bold text-slate-700">{inv.invoiceNumber}</code></td>
                                                    <td className="px-4 py-3 text-sm font-medium text-slate-700">{inv.tenantName}</td>
                                                    <td className="px-4 py-3 text-right text-sm font-bold text-emerald-600">{formatNaira(inv.amount)}</td>
                                                    <td className="px-4 py-3 text-center"><Badge variant={sc.variant} size="sm">{sc.label}</Badge></td>
                                                    <td className="px-4 py-3 hidden md:table-cell text-xs text-slate-500">{formatDate(inv.dueDate)}</td>
                                                    <td className="px-4 py-3 text-right">
                                                        <div className="flex items-center justify-end gap-1">
                                                            {(inv.status === 'pending' || inv.status === 'overdue') && (
                                                                <>
                                                                    <button onClick={() => sendWhatsAppReminder(inv)}
                                                                        className="p-1.5 rounded-lg hover:bg-emerald-50 text-slate-400 hover:text-emerald-600 transition-colors" title="WhatsApp Payment Reminder">
                                                                        <MessageSquare size={14} />
                                                                    </button>
                                                                    <button onClick={() => sendInvoiceMutation.mutate(inv.id)}
                                                                        className="p-1.5 rounded-lg hover:bg-blue-50 text-slate-400 hover:text-blue-600 transition-colors" title="Send Invoice Email">
                                                                        <Send size={14} />
                                                                    </button>
                                                                </>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </>
                )}

                {/* PLAN & BANK SETTINGS TAB */}
                {activeTab === 'settings' && (
                    <PlatformSettingsTab />
                )}

                {/* CREATE INVOICE MODAL */}
                <AnimatePresence>
                    {showInvoiceModal && (
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
                            onClick={() => setShowInvoiceModal(false)}>
                            <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }}
                                onClick={e => e.stopPropagation()} className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden">
                                <div className="bg-gradient-to-r from-violet-600 to-purple-700 px-6 py-4">
                                    <div className="flex items-center justify-between">
                                        <h2 className="text-lg font-bold text-white">Create Invoice</h2>
                                        <button onClick={() => setShowInvoiceModal(false)} className="p-2 rounded-xl bg-white/20 text-white"><X size={16} /></button>
                                    </div>
                                </div>
                                <div className="p-6 space-y-4">
                                    <div className="relative">
                                        <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Tenant *</label>
                                        {selectedTenant ? (
                                            <div className="flex items-center justify-between p-3 bg-emerald-50 border border-emerald-200 rounded-xl">
                                                <div className="flex items-center gap-2">
                                                    <Building2 size={14} className="text-emerald-600" />
                                                    <span className="text-sm font-bold text-emerald-700">{selectedTenant.name}</span>
                                                </div>
                                                <button onClick={() => setInvoiceForm({ ...invoiceForm, tenantId: '' })}
                                                    className="p-1 text-emerald-400 hover:text-rose-500"><X size={14} /></button>
                                            </div>
                                        ) : (
                                            <>
                                                <div className="relative">
                                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                                    <input type="text" placeholder="Search tenant..." value={tenantSearch}
                                                        onChange={e => { setTenantSearch(e.target.value); setShowTenantDropdown(true); }}
                                                        onFocus={() => setShowTenantDropdown(true)}
                                                        className="w-full pl-10 pr-4 py-2.5 border-2 border-slate-200 rounded-xl text-sm" />
                                                </div>
                                                {showTenantDropdown && filteredTenants.length > 0 && (
                                                    <div className="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-xl shadow-xl max-h-48 overflow-y-auto">
                                                        {filteredTenants.slice(0, 20).map(t => (
                                                            <button key={t.id} type="button"
                                                                onClick={() => { setInvoiceForm({ ...invoiceForm, tenantId: t.id }); setShowTenantDropdown(false); setTenantSearch(''); }}
                                                                className="w-full text-left px-4 py-2.5 text-sm hover:bg-violet-50 flex items-center gap-2">
                                                                <Building2 size={14} className="text-violet-400" />
                                                                <div><p className="font-medium">{t.name}</p><code className="text-[10px] text-slate-400">{t.slug}</code></div>
                                                            </button>
                                                        ))}
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Amount *</label>
                                        <input type="number" value={invoiceForm.amount || ''} onChange={e => setInvoiceForm({ ...invoiceForm, amount: parseInt(e.target.value) || 0 })}
                                            className="w-full px-4 py-2.5 border-2 border-slate-200 rounded-xl text-sm" placeholder="0" />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Due Date</label>
                                        <input type="date" value={invoiceForm.dueDate} onChange={e => setInvoiceForm({ ...invoiceForm, dueDate: e.target.value })}
                                            className="w-full px-4 py-2.5 border-2 border-slate-200 rounded-xl text-sm" />
                                    </div>
                                </div>
                                <div className="flex justify-end gap-3 px-6 py-4 bg-slate-50 border-t">
                                    <button onClick={() => setShowInvoiceModal(false)}
                                        className="px-5 py-2.5 border-2 border-slate-300 rounded-xl font-medium text-sm">Cancel</button>
                                    <button onClick={() => createInvoiceMutation.mutate(invoiceForm)}
                                        disabled={!invoiceForm.tenantId || invoiceForm.amount <= 0 || createInvoiceMutation.isPending}
                                        className="px-6 py-2.5 bg-gradient-to-r from-violet-600 to-purple-700 text-white rounded-xl font-semibold text-sm shadow-lg disabled:opacity-50 flex items-center gap-2">
                                        {createInvoiceMutation.isPending ? <Spinner size="xs" variant="white" /> : 'Create Invoice'}
                                    </button>
                                </div>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </div>
    );
}

// ============================================================
// PLATFORM PLAN & PAYMENT SETTINGS TAB
// ============================================================

function PlatformSettingsTab() {
    const queryClient = useQueryClient();
    const addToast = useToastStore(s => s.addToast);

    const { data: settingsData, isLoading } = useQuery({
        queryKey: ['super-admin-platform-settings'],
        queryFn: async () => {
            const { data } = await apiClient.get('/super-admin/platform/subscription-settings');
            return data?.data;
        },
    });

    const [form, setForm] = useState<any>(null);

    // Sync query data into editable state
    useEffect(() => {
        if (settingsData) {
            setForm(JSON.parse(JSON.stringify(settingsData)));
        }
    }, [settingsData]);

    const saveMutation = useMutation({
        mutationFn: (payload: any) => apiClient.post('/super-admin/platform/subscription-settings', payload),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['super-admin-platform-settings'] });
            queryClient.invalidateQueries({ queryKey: ['platform-subscription-settings'] });
            addToast({ type: 'success', title: 'Settings Saved', message: 'Platform subscription and bank settings have been updated.' });
        },
        onError: (err: any) => addToast({ type: 'error', title: 'Failed to Save', message: err?.message }),
    });

    if (isLoading || !form) {
        return (
            <div className="flex items-center justify-center py-20">
                <Spinner size="lg" />
            </div>
        );
    }

    const handleFeaturePriceChange = (id: string, price: number) => {
        setForm({
            ...form,
            featurePricing: form.featurePricing.map((f: any) =>
                f.id === id ? { ...f, monthlyPrice: price } : f
            ),
        });
    };

    const handleStandardPlanChange = (id: string, field: 'monthlyPrice' | 'yearlyPrice', val: number) => {
        setForm({
            ...form,
            standardPlans: form.standardPlans.map((p: any) =>
                p.id === id ? { ...p, [field]: val } : p
            ),
        });
    };

    const handleMobilePlanChange = (id: string, field: 'price' | 'productCap', val: number) => {
        setForm({
            ...form,
            mobilePlans: (form.mobilePlans || []).map((p: any) =>
                p.id === id ? { ...p, [field]: val } : p
            ),
        });
    };

    const handleBackupCycleChange = (cycle: string, price: number) => {
        setForm({
            ...form,
            localInstallation: {
                ...form.localInstallation,
                withBackup: {
                    ...form.localInstallation.withBackup,
                    cycles: form.localInstallation.withBackup.cycles.map((c: any) =>
                        c.cycle === cycle ? { ...c, price } : c
                    ),
                },
            },
        });
    };

    return (
        <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-5 bg-white rounded-2xl border border-slate-200 shadow-sm">
                <div>
                    <h2 className="text-lg font-bold text-slate-900">Platform Subscription & Billing Settings</h2>
                    <p className="text-xs text-slate-500">Configure official bank payment accounts, feature add-on rates, and local installation terms displayed on the public /subscribe page.</p>
                </div>
                <button
                    onClick={() => saveMutation.mutate(form)}
                    disabled={saveMutation.isPending}
                    className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-emerald-600 to-teal-700 text-white rounded-xl font-bold text-xs shadow-md shadow-emerald-600/20 hover:from-emerald-700 transition-all disabled:opacity-50 flex-shrink-0"
                >
                    {saveMutation.isPending ? <Spinner size="xs" variant="white" /> : <Save size={15} />}
                    {saveMutation.isPending ? 'Saving Settings...' : 'Save Settings'}
                </button>
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
                {/* 1. BANK & PROOF DETAILS */}
                <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm space-y-4">
                    <div className="flex items-center gap-2 pb-3 border-b border-slate-100">
                        <Building2 size={18} className="text-emerald-600" />
                        <h3 className="text-sm font-bold text-slate-900">Bank Account & Proof of Payment</h3>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-3">
                        <div>
                            <label className="block text-[11px] font-bold text-slate-500 uppercase mb-1">Bank Name</label>
                            <input
                                type="text"
                                value={form.bankDetails?.bankName || ''}
                                onChange={e => setForm({ ...form, bankDetails: { ...form.bankDetails, bankName: e.target.value } })}
                                className="w-full px-3 py-2 border border-slate-200 rounded-xl text-xs bg-slate-50 focus:bg-white focus:border-emerald-500"
                            />
                        </div>
                        <div>
                            <label className="block text-[11px] font-bold text-slate-500 uppercase mb-1">Account Number</label>
                            <input
                                type="text"
                                value={form.bankDetails?.accountNumber || ''}
                                onChange={e => setForm({ ...form, bankDetails: { ...form.bankDetails, accountNumber: e.target.value } })}
                                className="w-full px-3 py-2 border border-slate-200 rounded-xl text-xs bg-slate-50 focus:bg-white focus:border-emerald-500 font-mono font-bold"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-[11px] font-bold text-slate-500 uppercase mb-1">Account Name</label>
                        <input
                            type="text"
                            value={form.bankDetails?.accountName || ''}
                            onChange={e => setForm({ ...form, bankDetails: { ...form.bankDetails, accountName: e.target.value } })}
                            className="w-full px-3 py-2 border border-slate-200 rounded-xl text-xs bg-slate-50 focus:bg-white focus:border-emerald-500"
                        />
                    </div>

                    <div className="grid sm:grid-cols-2 gap-3">
                        <div>
                            <label className="block text-[11px] font-bold text-slate-500 uppercase mb-1">Proof of Payment Email</label>
                            <input
                                type="email"
                                value={form.bankDetails?.proofEmail || ''}
                                onChange={e => setForm({ ...form, bankDetails: { ...form.bankDetails, proofEmail: e.target.value } })}
                                className="w-full px-3 py-2 border border-slate-200 rounded-xl text-xs bg-slate-50 focus:bg-white focus:border-emerald-500 font-mono"
                            />
                        </div>
                        <div>
                            <label className="block text-[11px] font-bold text-slate-500 uppercase mb-1">Support / Urgent Phone</label>
                            <input
                                type="text"
                                value={form.bankDetails?.supportPhone || ''}
                                onChange={e => setForm({ ...form, bankDetails: { ...form.bankDetails, supportPhone: e.target.value } })}
                                className="w-full px-3 py-2 border border-slate-200 rounded-xl text-xs bg-slate-50 focus:bg-white focus:border-emerald-500 font-mono"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-[11px] font-bold text-slate-500 uppercase mb-1">Payment Reference Instructions</label>
                        <textarea
                            rows={2}
                            value={form.bankDetails?.paymentInstructions || ''}
                            onChange={e => setForm({ ...form, bankDetails: { ...form.bankDetails, paymentInstructions: e.target.value } })}
                            className="w-full px-3 py-2 border border-slate-200 rounded-xl text-xs bg-slate-50 focus:bg-white focus:border-emerald-500"
                        />
                    </div>
                </div>

                {/* 2. STANDARD PACKAGES PRICING */}
                <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm space-y-4">
                    <div className="flex items-center gap-2 pb-3 border-b border-slate-100">
                        <CreditCard size={18} className="text-emerald-600" />
                        <h3 className="text-sm font-bold text-slate-900">Standard Package Pricing</h3>
                    </div>

                    {form.standardPlans?.map((plan: any) => (
                        <div key={plan.id} className="p-4 rounded-xl border border-slate-200 bg-slate-50 space-y-3">
                            <div className="flex items-center justify-between">
                                <span className="text-xs font-bold text-slate-900">{plan.name}</span>
                                <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">{plan.businessModel} model</span>
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Monthly Fee (₦)</label>
                                    <input
                                        type="number"
                                        value={plan.monthlyPrice || 0}
                                        onChange={e => handleStandardPlanChange(plan.id, 'monthlyPrice', parseInt(e.target.value) || 0)}
                                        className="w-full px-3 py-2 border border-slate-200 rounded-xl text-xs bg-white font-mono font-bold"
                                    />
                                </div>
                                <div>
                                    <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Annual Fee (₦)</label>
                                    <input
                                        type="number"
                                        value={plan.yearlyPrice || 0}
                                        onChange={e => handleStandardPlanChange(plan.id, 'yearlyPrice', parseInt(e.target.value) || 0)}
                                        className="w-full px-3 py-2 border border-slate-200 rounded-xl text-xs bg-white font-mono font-bold"
                                    />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* 3. FEATURE-BASED ADD-ONS PRICING */}
                <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm space-y-4">
                    <div className="flex items-center gap-2 pb-3 border-b border-slate-100">
                        <Sliders size={18} className="text-emerald-600" />
                        <h3 className="text-sm font-bold text-slate-900">Feature-Based Custom Module Rates</h3>
                    </div>

                    <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
                        {form.featurePricing?.map((feat: any) => (
                            <div key={feat.id} className="flex items-center justify-between p-3 rounded-xl border border-slate-100 bg-slate-50/60 hover:bg-slate-50">
                                <div className="min-w-0 pr-3">
                                    <p className="text-xs font-bold text-slate-800">{feat.name}</p>
                                    <span className="text-[10px] text-slate-400">{feat.category}</span>
                                </div>
                                <div className="flex items-center gap-1.5 flex-shrink-0">
                                    <span className="text-xs text-slate-500 font-semibold">₦</span>
                                    <input
                                        type="number"
                                        value={feat.monthlyPrice || 0}
                                        onChange={e => handleFeaturePriceChange(feat.id, parseInt(e.target.value) || 0)}
                                        className="w-24 px-2.5 py-1.5 border border-slate-200 rounded-lg text-xs bg-white font-mono font-bold text-right"
                                    />
                                    <span className="text-[10px] text-slate-400">/mo</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* 4. LOCAL INSTALLATION & CLOUD BACKUP CYCLES */}
                <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm space-y-4">
                    <div className="flex items-center gap-2 pb-3 border-b border-slate-100">
                        <HardDrive size={18} className="text-emerald-600" />
                        <h3 className="text-sm font-bold text-slate-900">Local Installation & Backup SLA Pricing</h3>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Base On-Site Setup (₦)</label>
                            <input
                                type="number"
                                value={form.localInstallation?.baseSetupPrice || 0}
                                onChange={e => setForm({
                                    ...form,
                                    localInstallation: {
                                        ...form.localInstallation,
                                        baseSetupPrice: parseInt(e.target.value) || 0,
                                        withoutBackup: { ...form.localInstallation.withoutBackup, price: parseInt(e.target.value) || 0 },
                                        withBackup: { ...form.localInstallation.withBackup, setupPrice: parseInt(e.target.value) || 0 },
                                    },
                                })}
                                className="w-full px-3 py-2 border border-slate-200 rounded-xl text-xs bg-slate-50 font-mono font-bold"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-[10px] font-bold text-slate-500 uppercase mb-2">Cloud Backup Sync Cycle Fees</label>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                            {form.localInstallation?.withBackup?.cycles?.map((c: any) => (
                                <div key={c.cycle} className="p-2.5 rounded-xl border border-slate-200 bg-slate-50">
                                    <span className="text-[10px] font-bold text-slate-600 block">{c.label}</span>
                                    <div className="flex items-center gap-1 mt-1">
                                        <span className="text-[10px] text-slate-400">₦</span>
                                        <input
                                            type="number"
                                            value={c.price || 0}
                                            onChange={e => handleBackupCycleChange(c.cycle, parseInt(e.target.value) || 0)}
                                            className="w-full px-2 py-1 border border-slate-200 rounded-lg text-xs bg-white font-mono font-bold text-right"
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* 5. MOBILE APPLICATION TIERS & PRODUCT CAPS */}
                <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm space-y-4">
                    <div className="flex items-center gap-2 pb-3 border-b border-slate-100">
                        <Smartphone size={18} className="text-teal-600" />
                        <div>
                            <h3 className="text-sm font-bold text-slate-900">Mobile Standalone Packages & Product Caps</h3>
                            <p className="text-[11px] text-slate-500">Configure subscription fees and inventory product limits for small business mobile merchants.</p>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        {(form.mobilePlans || []).map((plan: any) => (
                            <div key={plan.id} className="p-4 rounded-xl border border-slate-200 bg-slate-50 space-y-3">
                                <div className="flex items-center justify-between">
                                    <span className="text-xs font-bold text-slate-900">{plan.name}</span>
                                    <span className="text-[10px] font-semibold text-teal-700 bg-teal-50 px-2 py-0.5 rounded-full border border-teal-200">
                                        {plan.billingCycle}
                                    </span>
                                </div>

                                <div className="space-y-2">
                                    <div>
                                        <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Monthly Fee (₦)</label>
                                        <input
                                            type="number"
                                            value={plan.price || 0}
                                            onChange={e => handleMobilePlanChange(plan.id, 'price', parseInt(e.target.value) || 0)}
                                            className="w-full px-3 py-2 border border-slate-200 rounded-xl text-xs bg-white font-mono font-bold"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Product Cap (-1 = Unlimited)</label>
                                        <input
                                            type="number"
                                            value={plan.productCap ?? -1}
                                            onChange={e => handleMobilePlanChange(plan.id, 'productCap', parseInt(e.target.value) || 0)}
                                            className="w-full px-3 py-2 border border-slate-200 rounded-xl text-xs bg-white font-mono font-bold"
                                        />
                                    </div>
                                </div>
                                <div className="pt-2 border-t border-slate-200 flex items-center justify-between text-[10px] text-slate-500">
                                    <span>Max Staff: 1 (Cashier)</span>
                                    <span>Single Branch</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}